import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ticket } from 'lucide-react';
import { CartItem } from '@/types';
import { PRICES, riderKey } from '@/utils/cart';
import { usePassPhotos } from '@/components/EventPassTicket';
import { EventPassGallery } from '@/components/EventPassGallery';
import { trackEventPass } from '@/utils/eventPassTracking';

export const chf = (n: number) => (Number.isInteger(n) ? `CHF ${n.toLocaleString('de-CH').replace(/’/g, "'")}.-` : `CHF ${n.toFixed(2)}`);

/** Total list price minus pass price, summed across every Event Pass in the cart. */
export const passSavings = (items: CartItem[]) =>
  items.filter(i => i.type === 'event_pass').reduce((s, i) => {
    const full = (i.passEntries?.length || 0) * PRICES.COLLECTION;
    return s + Math.max(0, full - i.price);
  }, 0);

const classLabel = (entries: { raceCategory: string }[]) =>
  Object.entries(entries.reduce<Record<string, number>>((m, e) => { m[e.raceCategory] = (m[e.raceCategory] || 0) + 1; return m; }, {}))
    .map(([c, n]) => (n > 1 ? `${c} ×${n}` : c)).join(' · ');

/** Fanned photo stack: one preview per class, slightly rotated. */
export const PassFanStack: React.FC<{ urls: string[]; className?: string }> = ({ urls, className = '' }) => {
  const shown = urls.slice(0, 3);
  return (
    <div className={`relative h-16 w-20 shrink-0 ${className}`} aria-hidden>
      {shown.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center rounded-md bg-surface-container-high text-primary">
          <Ticket className="h-5 w-5" />
        </div>
      ) : shown.map((u, i) => {
        const o = i - (shown.length - 1) / 2;
        return (
          <img
            key={u}
            src={u}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-16 w-20 rounded-md object-cover outline outline-1 -outline-offset-1 outline-black/10 shadow-sm"
            style={{ transform: `translateX(${o * 6}px) rotate(${o * 4}deg)`, zIndex: i }}
          />
        );
      })}
    </div>
  );
};

export const CartPassLine: React.FC<{ item: CartItem }> = ({ item }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const entries = item.passEntries || [];
  const key = riderKey(item.swissEquestrianRiderId, item.participantName);
  const { data: classes } = usePassPhotos(key, entries, item.eventId || '');
  const urls = (classes || []).map(c => c.urls[0]).filter(Boolean) as string[];
  const photos = (classes || []).reduce((s, c) => s + c.urls.length, 0);
  const full = entries.length * PRICES.COLLECTION;

  return (
    <div className="flex min-w-0 flex-1 items-center gap-4">
      <button
        type="button"
        onClick={() => { setOpen(true); trackEventPass('gallery_opened', { event_id: item.eventId, source: 'cart', from: 'cart_stack', classes: entries.length, photos }); }}
        className="mx-1 rounded-md outline-none transition-transform duration-150 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 enabled:active:scale-[0.96]"
        aria-label={t('eventPass.viewPhotos')}
      >
        <PassFanStack urls={urls} />
      </button>
      <div className="min-w-0">
        <h4 className="font-serif text-lg italic leading-tight text-primary [text-wrap:balance]">{item.participantName}</h4>
        <p className="mt-0.5 text-sm tabular-nums text-muted-foreground">
          Event Pass · {photos > 0 ? t('eventPass.stats', { races: entries.length, photos }) : t('eventPass.cartLabel', { count: entries.length }).replace(/^Event Pass · /, '')}
        </p>
        <p className="text-sm text-muted-foreground [text-wrap:pretty]">{classLabel(entries)}</p>
        <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-sm tabular-nums">
          <span className="font-semibold text-foreground">{chf(item.price)}</span>
          {full > item.price && <span className="text-muted-foreground line-through">{chf(full)}</span>}
        </p>
      </div>
      {classes && (
        <EventPassGallery
          open={open}
          onOpenChange={setOpen}
          riderName={item.participantName}
          classes={classes}
          inCart
          onAdd={() => setOpen(false)}
          eventId={item.eventId}
          eventName={item.eventName}
          source="cart"
        />
      )}
    </div>
  );
};

/** List subtotal, pass savings and total — shared by the cart page and the drawer. */
export const CartTotals: React.FC<{ items: CartItem[]; total: number }> = ({ items, total }) => {
  const { t } = useTranslation();
  const savings = passSavings(items);
  return (
    <dl className="space-y-1.5 tabular-nums">
      {savings > 0 && (
        <>
          <div className="flex justify-between text-sm text-muted-foreground">
            <dt>{t('cart.listSubtotal')}</dt>
            <dd className="line-through">{chf(total + savings)}</dd>
          </div>
          <div className="flex justify-between text-sm font-semibold text-success">
            <dt>{t('cart.passSavings')}</dt>
            <dd>− {chf(savings)}</dd>
          </div>
        </>
      )}
      <div className="flex items-baseline justify-between pt-1">
        <dt className="font-medium">{t('cart.total')}</dt>
        <dd className="font-serif text-2xl text-primary">{chf(total)}</dd>
      </div>
    </dl>
  );
};
